import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
// v2 storage key — previous value ("sb_prefs") was used when :root and .dark
// were the same palette. Bumping the key ensures returning visitors who
// never explicitly chose a theme fall through to the new default ("dark")
// instead of inheriting the stale, now-visible "light" preference. Users
// who toggle from now on persist their choice in the v2 key going forward.
const STORAGE_KEY = "sb_prefs_v2";

const DEFAULT_PREFS = {
  theme: "dark", // dark is the initial brand experience — user can opt into light
  large_text: false,
  high_contrast: false,
  reduce_motion: false,
  colorblind_mode: "off",
  notifications_email: true,
};

function readInitialPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {}
  return DEFAULT_PREFS;
}

function applyPrefsToDom(prefs) {
  const html = document.documentElement;
  const isSystemDark =
    prefs.theme === "system" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  const dark = prefs.theme === "dark" || isSystemDark;
  html.classList.toggle("dark", dark);
  html.dataset.largeText = String(prefs.large_text);
  html.dataset.highContrast = String(prefs.high_contrast);
  html.dataset.reduceMotion = String(prefs.reduce_motion);
  html.dataset.colorblindMode = prefs.colorblind_mode;
}

export function ThemeProvider({ children }) {
  const [prefs, setPrefs] = useState(readInitialPrefs);

  useEffect(() => {
    applyPrefsToDom(prefs);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const updatePrefs = useCallback((patch) => {
    setPrefs((p) => ({ ...p, ...patch }));
  }, []);

  const setTheme = useCallback((theme) => updatePrefs({ theme }), [updatePrefs]);

  return (
    <ThemeContext.Provider value={{ prefs, updatePrefs, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
